import { message } from 'antd';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { appLog } from '~src/utils/log';
import { formatDate } from '~src/utils/formatDate';
import { DB_STATE } from '~src/types/enums';
import { DbQuestion } from '~src/types/db';
import { exportQuestionsCsv } from '~src/services/api/questions';

type UseQuestionsExportParams = {
  projectId?: number;
  tenderId: string;
};

export const useQuestionsExport = ({
  projectId,
  tenderId,
}: UseQuestionsExportParams) => {
  const intl = useIntl();

  /**
   * Copy filtered questions to clipboard as TSV (existing behavior)
   * Maintains compatibility with current implementation
   */
  const handleCopyToClipboard = useCallback(
    async (filteredQuestions: DbQuestion[]) => {
      try {
        const validQuestions = filteredQuestions.filter(
          (q) =>
            q.state !== DB_STATE.REJECTED &&
            q.state !== DB_STATE.REMOVED &&
            !q.report_data?.is_ignore
        );

        if (!validQuestions.length) {
          message.info(intl.formatMessage({ id: 'qa-copy-to-excel-empty' }));
          return;
        }

        const headers = [
          intl.formatMessage({ id: 'qa-excel-header-prompt-time' }),
          intl.formatMessage({ id: 'qa-excel-header-question' }),
          intl.formatMessage({ id: 'qa-excel-header-answer-time' }),
          intl.formatMessage({ id: 'qa-excel-header-reply' }),
        ].join('\t');

        const rows = validQuestions
          .flatMap((question) => {
            const questionTime = formatDate(question.created_at, 'long');
            const questionText = (question.question || '')
              .replace(/\t/g, ' ')
              .replace(/\n/g, ' ')
              .replace(/\r/g, '');

            const validAnswers = (question.answers || []).filter(
              (a) =>
                a.state !== DB_STATE.REJECTED && a.state !== DB_STATE.REMOVED
            );

            if (!validAnswers.length) {
              return `${questionTime}\t${questionText}\t\t`;
            }

            return validAnswers.map((answer) => {
              const answerTime = formatDate(answer.created_at, 'long');
              const answerText = (answer.answer || '')
                .replace(/\t/g, ' ')
                .replace(/\n/g, ' ')
                .replace(/\r/g, '');
              return `${questionTime}\t${questionText}\t${answerTime}\t${answerText}`;
            });
          })
          .join('\n');

        const tsvContent = `${headers}\n${rows}`;

        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(tsvContent);
        } else {
          const textArea = document.createElement('textarea');
          textArea.value = tsvContent;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }

        message.success(intl.formatMessage({ id: 'qa-copy-to-excel-success' }));
      } catch (error) {
        appLog('handleCopyToClipboard error:', error);
        message.error(intl.formatMessage({ id: 'failed' }));
      }
    },
    [intl]
  );

  /**
   * Download all questions as CSV file via backend
   */
  const handleDownloadCsv = useCallback(async () => {
    if (!projectId) {
      message.error(intl.formatMessage({ id: 'missing-project-id' }));
      return;
    }

    try {
      const response = await exportQuestionsCsv({
        projectId,
        tenderId,
        responseType: 'csv',
      });

      const contentDisposition = response.headers['content-disposition'];
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch?.[1] || `tender-${tenderId}-questions.csv`;

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);

      message.success(intl.formatMessage({ id: 'qa-download-csv-success' }));
    } catch (error) {
      appLog('handleDownloadCsv error:', error);
      message.error(intl.formatMessage({ id: 'failed' }));
    }
  }, [projectId, tenderId, intl]);

  /**
   * Copy all questions as CSV to clipboard via backend
   */
  const handleCopyCsvInline = useCallback(async () => {
    if (!projectId) {
      message.error(intl.formatMessage({ id: 'missing-project-id' }));
      return;
    }

    try {
      const response = await exportQuestionsCsv({
        projectId,
        tenderId,
        responseType: 'csv_inline',
      });

      const csvContent = await response.data.text();

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(csvContent);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = csvContent;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      message.success(intl.formatMessage({ id: 'qa-copy-csv-success' }));
    } catch (error) {
      appLog('handleCopyCsvInline error:', error);
      message.error(intl.formatMessage({ id: 'failed' }));
    }
  }, [projectId, tenderId, intl]);

  return {
    handleCopyToClipboard,
    handleDownloadCsv,
    handleCopyCsvInline,
  };
};
